/**
 * «В работе» на экране «Лиды»: лиды в статусе «В работе», их телефон и
 * WhatsApp — ушла ли рассылка новому лиду, дошла ли, прочитали ли, нажали ли
 * кнопку, ответили ли. Спека `docs/specs/2026-09-25-wazzup-design.md`; числа —
 * те же, что у `prokat-runner leads-inwork`.
 */
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { useSearchParams } from "react-router";

import { LeadLink } from "@/components/LeadLink";
import {
  type InWorkCall,
  type InWorkOutcome,
  type InWorkPhone,
  type LeadsInWork,
  loadLeadsInWork,
  type WaTick,
} from "@/lib/leadInWork";
import { shortTime } from "@/lib/leads";

const DAYS = [7, 30, 90] as const;

const OUTCOME: Record<
  InWorkOutcome,
  { label: string; hint: string; tone: string }
> = {
  replied: {
    label: "Ответил",
    hint: "клиент написал в WhatsApp после создания лида",
    tone: "text-ok",
  },
  button: {
    label: "Нажал кнопку",
    hint: "ответ кнопкой на рассылку: «Актуально» / «Не актуально»",
    tone: "text-ok",
  },
  read: {
    label: "Прочитал",
    hint: "рассылка прочитана, ответа нет",
    tone: "text-fg",
  },
  delivered: {
    label: "Доставлено",
    hint: "рассылка доставлена, не прочитана",
    tone: "text-fg",
  },
  sent: {
    label: "Отправлено",
    hint: "ушла, статуса доставки нет (до подключения вебхука статусов не было)",
    tone: "text-fg-muted",
  },
  failed: {
    label: "Не дошло",
    hint: "ошибка отправки или номера нет в WhatsApp",
    tone: "text-err",
  },
  queued: {
    label: "Ждёт отправки",
    hint: "в очереди рассылки CRM",
    tone: "text-warn",
  },
  none: {
    label: "Рассылки нет",
    hint: "номер есть, рассылки по лиду не было",
    tone: "text-warn",
  },
};

/** Галочки как в WhatsApp — значок И слово: одним цветом состояние не передаём. */
const TICK: Record<WaTick, { mark: string; label: string; tone: string }> = {
  read: { mark: "✓✓", label: "прочитано", tone: "text-accent" },
  delivered: { mark: "✓✓", label: "доставлено", tone: "text-fg-muted" },
  sent: { mark: "✓", label: "отправлено", tone: "text-fg-muted" },
  error: { mark: "✕", label: "ошибка", tone: "text-err" },
};

/** Три фильтра по галочкам самой рассылки «Лид 1» — главные для дожима. */
const MAIL_FILTERS = [
  {
    id: "sent",
    mark: "✓",
    label: "Одна галочка",
    hint: "«Лид 1» отправлен, но не доставлен — телефон вне WhatsApp или выключен",
    tone: "text-warn",
  },
  {
    id: "error",
    mark: "✕",
    label: "Не отправилось",
    hint: "красная иконка: Wazzup не смог доставить «Лид 1» (нет WhatsApp, лимит маркетинга, ошибка)",
    tone: "text-err",
  },
  {
    id: "delivered",
    mark: "✓✓",
    label: "Не прочитал",
    hint: "«Лид 1» доставлен (две серые галочки), но не прочитан",
    tone: "text-fg",
  },
] as const;
type MailFilter = `m_${(typeof MAIL_FILTERS)[number]["id"]}`;

type Filter = InWorkOutcome | "unreachable" | MailFilter | null;
const ORDER = Object.keys(OUTCOME) as InWorkOutcome[];

function Tile({
  o,
  n,
  active,
  onClick,
}: {
  o: InWorkOutcome;
  n: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={OUTCOME[o].hint}
      aria-pressed={active}
      className={`flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border p-3 text-left ${
        active
          ? "border-accent bg-bg-hover"
          : "border-border bg-bg-subtle hover:bg-bg-hover"
      }`}
      data-annot={`итог «${OUTCOME[o].label}»`}
    >
      <div className="text-[12px] text-fg-muted">{OUTCOME[o].label}</div>
      <div
        className={`text-[22px] font-semibold leading-none tabular-nums ${n ? OUTCOME[o].tone : "text-fg-faint"}`}
      >
        {n}
      </div>
    </button>
  );
}

function Tick({ x }: { x: InWorkPhone }) {
  const w = x.wa;
  if (!w)
    return (
      <span
        className="text-fg-faint"
        title="ни вебхук, ни снимок переписки CRM об этом номере не знают"
      >
        нет данных
      </span>
    );
  const t = TICK[w.state];
  return (
    <div
      className="flex flex-col"
      title={
        w.detail ||
        (w.source === "webhook"
          ? "по вебхуку Wazzup"
          : "по снимку переписки CRM")
      }
    >
      <span className={`whitespace-nowrap ${t.tone}`}>
        <span aria-hidden="true" className="font-semibold">
          {t.mark}
        </span>{" "}
        {t.label}
      </span>
      {w.unreachable && (
        <span className="whitespace-nowrap text-[11.5px] font-medium text-err">
          не дошло
        </span>
      )}
      {w.detail && (
        <span className="max-w-[220px] truncate text-[11.5px] text-fg-faint">
          {w.detail}
        </span>
      )}
    </div>
  );
}

function Mailing({ x }: { x: InWorkPhone }) {
  const m = x.mailing;
  if (!m) return <span className="text-warn">не было</span>;
  const where = [m.in_crm && "очередь CRM", m.in_wazzup && "Wazzup"]
    .filter(Boolean)
    .join(" + ");
  return (
    <div
      className="flex flex-col"
      title={`рассылка «Лид 1» по номеру; найдена: ${where}`}
    >
      <span>
        {m.status || "отправлена"}
        {m.count > 1 && (
          <span className="text-fg-faint"> · {m.count} раза</span>
        )}
      </span>
      <span className="text-[11.5px] text-fg-faint tabular-nums">
        {m.sent
          ? `ушла ${shortTime(m.sent)}`
          : `в очереди с ${shortTime(m.queued)}`}
        {m.tick && ` · ${TICK[m.tick].mark} ${TICK[m.tick].label}`}
      </span>
    </div>
  );
}

const REASON: Record<string, string> = {
  client: "разговор с клиентом",
  autoanswer: "автоответчик",
  assistant: "голосовой помощник",
  monologue: "говорил один",
  silence: "тишина",
  no_transcript: "нет расшифровки",
  failed: "не записан",
};

/** Состояние звонка словами: «пропущен» / «соединились 9 с · тишина» / «разговор 1 мин 34 с». */
function callState(c: InWorkCall): string {
  if (!c.connected)
    return c.code === "304" ? "пропущен" : `не соединились (${c.code || "?"})`;
  const dur =
    c.dur >= 60
      ? `${Math.floor(c.dur / 60)} мин ${c.dur % 60} с`
      : `${c.dur} с`;
  if (c.talk === "talk") return `разговор ${dur}`;
  return `соединились ${dur}${c.reason ? ` · ${REASON[c.reason] ?? c.reason}` : ""}`;
}

function Calls({
  x,
  open,
  onToggle,
}: {
  x: InWorkPhone;
  open: boolean;
  onToggle: () => void;
}) {
  const c = x.calls;
  if (!c) return <span className="text-fg-faint">нет звонков</span>;
  const texts = [c.first_in, c.best].filter((v) => v?.text).length;
  return (
    <div className="flex flex-col gap-0.5">
      {c.first_in && (
        <span
          className={`whitespace-nowrap ${c.first_in.talk === "talk" ? "text-ok" : c.first_in.connected ? "" : "text-warn"}`}
          title={`входящий, с которого начался лид · линия ${c.first_in.line}`}
        >
          вх. {callState(c.first_in)}
        </span>
      )}
      <span className="whitespace-nowrap text-[11.5px] text-fg-faint tabular-nums">
        исх {c.out}, соединились {c.out_connected} · разговоров {c.talks}
      </span>
      {texts > 0 && (
        <button
          type="button"
          onClick={onToggle}
          className="self-start text-[11.5px] text-accent hover:underline"
          aria-expanded={open}
        >
          {open ? "скрыть расшифровку" : "прочитать расшифровку"}
        </button>
      )}
    </div>
  );
}

function Transcript({ c, title }: { c: InWorkCall; title: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[11.5px] text-fg-faint tabular-nums">
        {title} · {shortTime(c.at)} · {callState(c)} · линия {c.line}
      </div>
      <p className="max-w-[900px] whitespace-pre-wrap text-[12.5px] leading-relaxed">
        {c.text}
      </p>
    </div>
  );
}

function Leads({ x }: { x: InWorkPhone }) {
  return (
    <div className="flex flex-col gap-0.5">
      {x.leads.map((l) => (
        <div key={l.lead_id} className="whitespace-nowrap">
          <span className="font-mono tabular-nums">
            <LeadLink id={l.lead_id} />
          </span>{" "}
          <span className="text-[11.5px] text-fg-faint tabular-nums">
            {shortTime(l.created)}
          </span>
        </div>
      ))}
    </div>
  );
}

function Table({ rows }: { rows: InWorkPhone[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const td = "px-3 py-1.5 align-top";
  return (
    <div className="max-h-[640px] overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
      <table className="w-full text-[12.5px]">
        <thead className="sticky top-0 bg-bg-subtle text-left text-[11.5px] text-fg-faint">
          <tr>
            <th className={th}>Телефон</th>
            <th
              className={th}
              title="лиды номера в статусе «В работе», свежий первым"
            >
              Лиды
            </th>
            <th className={th}>Источник</th>
            <th className={th}>Ответственный</th>
            <th className={th}>Итог</th>
            <th
              className={th}
              title="галочки последнего нашего сообщения этому номеру"
            >
              WhatsApp
            </th>
            <th
              className={th}
              title="рассылка «Лид 1» по номеру: очередь CRM (категория 49) и/или сам шаблон в Wazzup"
            >
              «Лид 1»
            </th>
            <th className={th}>Кнопка</th>
            <th
              className={th}
              title="входящий, с которого начался лид, наши перезвоны и был ли разговор по расшифровке"
            >
              Звонки
            </th>
            <th
              className={th}
              title="сообщения в переписке CRM после создания первого лида: входящих / исходящих"
            >
              Вх / исх
            </th>
            <th className={th}>След. обработка</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => {
            const last = x.leads[0];
            if (!last) return null; // у номера всегда есть лид — иначе его нет в списке
            const c = x.calls;
            return (
              <Fragment key={x.phone}>
                <tr
                  className="border-t border-border hover:bg-bg-hover"
                  data-annot={`номер ${x.phone}`}
                >
                  <td
                    className={`${td} whitespace-nowrap font-mono tabular-nums`}
                  >
                    {x.phone}
                  </td>
                  <td className={td}>
                    <Leads x={x} />
                  </td>
                  <td className={td}>{last.source}</td>
                  <td className={td}>
                    {last.responsible || (
                      <span className="text-fg-faint">—</span>
                    )}
                  </td>
                  <td
                    className={`${td} whitespace-nowrap ${OUTCOME[x.outcome].tone}`}
                  >
                    {OUTCOME[x.outcome].label}
                    {x.replied && (
                      <div className="text-[11.5px] text-fg-faint tabular-nums">
                        {shortTime(x.replied)}
                      </div>
                    )}
                  </td>
                  <td className={td}>
                    <Tick x={x} />
                  </td>
                  <td className={td}>
                    <Mailing x={x} />
                  </td>
                  <td className={`${td} whitespace-nowrap`}>
                    {x.button ?? <span className="text-fg-faint">—</span>}
                  </td>
                  <td className={td}>
                    <Calls
                      x={x}
                      open={open === x.phone}
                      onToggle={() =>
                        setOpen(open === x.phone ? null : x.phone)
                      }
                    />
                  </td>
                  <td
                    className={`${td} text-right tabular-nums ${x.crm_in + x.crm_out === 0 ? "text-fg-faint" : ""}`}
                  >
                    {x.crm_in} / {x.crm_out}
                  </td>
                  <td
                    className={`${td} whitespace-nowrap tabular-nums text-fg-muted`}
                  >
                    {shortTime(last.next_work)}
                  </td>
                </tr>
                {open === x.phone && c && (
                  <tr className="border-t border-border bg-bg">
                    <td colSpan={11} className="px-4 py-3">
                      <div className="flex flex-col gap-3">
                        {c.first_in?.text && (
                          <Transcript
                            c={c.first_in}
                            title="Входящий, с которого начался лид"
                          />
                        )}
                        {c.best?.text && (
                          <Transcript
                            c={c.best}
                            title="Самый длинный разговор"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Body({ r }: { r: LeadsInWork }) {
  const [filter, setFilter] = useState<Filter>(null);
  const rows =
    filter === "unreachable"
      ? r.phones.filter((x) => x.wa?.unreachable)
      : filter?.startsWith("m_")
        ? r.phones.filter((x) => x.mailing?.tick === filter.slice(2))
        : filter
          ? r.phones.filter((x) => x.outcome === filter)
          : r.phones;
  const toggle = (f: Filter) => setFilter(filter === f ? null : f);
  return (
    <>
      <div className="text-[12.5px] text-fg-muted">
        Уникальных номеров — {r.total} (лидов в работе {r.leads_total}),
        рассылка «Лид 1» была {r.mailed}; свежие сверху
        {r.no_phone > 0 && (
          <span className="text-fg-faint">
            {" "}
            (лиды без номера не показаны: {r.no_phone})
          </span>
        )}
        .{" "}
        <span className="text-fg-faint">
          Галочки — из Wazzup (вебхук и выгрузка истории веб-интерфейса
          {r.webhook_since ? ` с ${shortTime(r.webhook_since)}` : ""}), иначе —
          из снимка переписки CRM.
        </span>
      </div>
      <div
        className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-border p-3"
        data-annot="фильтры рассылки «Лид 1»"
      >
        <div className="text-[12.5px] font-medium">
          Рассылка «Лид 1» по галочкам Wazzup{" "}
          <span className="font-normal text-fg-faint">
            — номеров с рассылкой {r.mailed}; прочитали {r.mailing_ticks.read}
            {r.mailing_ticks.unknown > 0 &&
              `, галочки неизвестны ${r.mailing_ticks.unknown}`}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {MAIL_FILTERS.map((f) => {
            const id: MailFilter = `m_${f.id}`;
            const n = r.mailing_ticks[f.id];
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => toggle(id)}
                aria-pressed={filter === id}
                title={f.hint}
                className={`flex items-center justify-between gap-3 rounded-[var(--radius-card)] border px-3 py-2.5 text-left ${
                  filter === id
                    ? "border-accent bg-bg-hover"
                    : "border-border bg-bg-subtle hover:bg-bg-hover"
                }`}
                data-annot={`фильтр «${f.label}»`}
              >
                <span className="flex items-center gap-2 text-[13px]">
                  <span
                    aria-hidden="true"
                    className={`font-semibold ${f.tone}`}
                  >
                    {f.mark}
                  </span>
                  {f.label}
                </span>
                <span
                  className={`text-[20px] font-semibold tabular-nums ${n ? f.tone : "text-fg-faint"}`}
                >
                  {n}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 md:grid-cols-5 xl:grid-cols-9">
        {ORDER.map((o) => (
          <Tile
            key={o}
            o={o}
            n={r.outcomes[o]}
            active={filter === o}
            onClick={() => toggle(o)}
          />
        ))}
        <button
          type="button"
          onClick={() => toggle("unreachable")}
          aria-pressed={filter === "unreachable"}
          title="клиент ни разу не писал, ничего не доставлено, последнее — красная галочка или одна галочка дольше часа: вероятно, номера нет в WhatsApp — писать в другой мессенджер"
          className={`flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border p-3 text-left ${
            filter === "unreachable"
              ? "border-err bg-bg-hover"
              : "border-border bg-bg-subtle hover:bg-bg-hover"
          }`}
          data-annot="фильтр «Не дошло в WhatsApp»"
        >
          <div className="text-[12px] text-fg-muted">
            Не дошло <span aria-hidden="true">✓ / ✕</span>
          </div>
          <div
            className={`text-[22px] font-semibold leading-none tabular-nums ${r.unreachable ? "text-err" : "text-fg-faint"}`}
          >
            {r.unreachable}
          </div>
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">
          {filter === "unreachable"
            ? "Недошедших сообщений нет."
            : "Номеров нет."}
        </div>
      ) : (
        <Table rows={rows} />
      )}
    </>
  );
}

export function LeadInWorkSection() {
  const [params, setParams] = useSearchParams();
  const raw = Number(params.get("days"));
  const days = (DAYS as readonly number[]).includes(raw) ? raw : 30;
  const q = useQuery({
    queryKey: ["leads-in-work", days],
    queryFn: () => loadLeadsInWork(days),
  });
  const setDays = (d: number) => {
    const out = new URLSearchParams(params);
    if (d === 30) out.delete("days");
    else out.set("days", String(d));
    setParams(out, { replace: true });
  };
  return (
    <section className="flex flex-col gap-3" data-annot="в работе">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[12px] text-fg-faint">
          лиды в статусе «В работе», созданные за последние
        </span>
        <div className="flex gap-1" role="group" aria-label="Период">
          {DAYS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              aria-pressed={days === d}
              className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
                days === d
                  ? "border-accent text-fg"
                  : "border-border text-fg-muted hover:text-fg"
              }`}
            >
              {d} дн
            </button>
          ))}
        </div>
      </div>
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю по CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">
          Не удалось получить лиды: {String(q.error.message)}
        </div>
      ) : (
        <Body key={days} r={q.data} />
      )}
    </section>
  );
}
